<template>
    <div class="mcq-paper">
        <div v-if="fetchData" class="mcq-loading">
            <b-spinner small variant="primary" class="mr-2"></b-spinner> Updating...
        </div>
        <div v-else class="no-print">
            <!-- Header  -->
            <div class="mcq-header">
                <div>
                    <div class="mcq-title">{{ exam.name }}</div>
                    <div class="mcq-subtitle">
                        <span class="mcq-chip">
                            {{ questionList.length }}
                            {{ questionList.length == 1 ? "Question" : "Questions" }}
                        </span>
                        <span class="mcq-chip mcq-chip-success" v-if="!differentScores">
                            +{{ examScores.positive_mark }} for right answer
                        </span>
                        <span class="mcq-chip mcq-chip-danger" v-if="!differentScores">
                            -{{ examScores.negative_mark == null ? 0 : examScores.negative_mark }}
                            for wrong answer
                        </span>
                        <span class="mcq-chip" v-else>Every question has its own score</span>
                    </div>
                </div>
                <div class="mcq-header-actions">
                    <b-button variant="primary" size="sm" class="mcq-btn" @click="addNewQuestion()">
                        <i class="fas fa-plus mr-1"></i> Add Question
                    </b-button>
                    <b-button variant="outline-success" size="sm" class="mcq-btn ml-2"
                        @click="$bvModal.show('mcqImportExcelModal')">
                        <i class="fas fa-file-excel mr-1"></i> Import Excel
                    </b-button>
                    <b-button variant="outline-danger" size="sm" class="mcq-btn ml-2" v-if="questionList.length"
                        @click="deleteAllQuestions(exam.id)">
                        <i class="fas fa-trash mr-1"></i> Delete all
                    </b-button>
                    <b-button variant="light" size="sm" class="mcq-icon-btn ml-2" title="Close"
                        @click="$emit('close')"><i class="fas fa-times"></i></b-button>
                </div>
            </div>

            <!-- Empty state  -->
            <div class="mcq-empty" v-if="!questionList.length">
                <i class="far fa-question-circle"></i>
                <div class="mcq-empty-title">No questions yet</div>
                <div class="mcq-empty-text">Add your first multiple choice question to start the question paper.</div>
                <b-button variant="primary" size="sm" class="mcq-btn mt-3" @click="addNewQuestion()">
                    <i class="fas fa-plus mr-1"></i> Add Question
                </b-button>
                <b-button variant="outline-success" size="sm" class="mcq-btn mt-3 ml-2"
                    @click="$bvModal.show('mcqImportExcelModal')">
                    <i class="fas fa-file-excel mr-1"></i> Import Excel
                </b-button>
            </div>

            <!-- Questions  -->
            <div class="mcq-list" v-if="questionList.length">
            <div class="mcq-card" v-for="question in questionList" :key="question.id">
                <div class="mcq-card-head">
                    <span class="mcq-qno-plain">{{ question.question_no }}.</span>
                    <div class="mcq-qtext">
                        <template v-if="hasQuestionText(question)">
                            <template v-for="(qData, qdIndex) in question.question">
                                <span :key="qdIndex" v-if="qData.type == 'text'"
                                    v-html="latexCodeToMathLive(qData.value)"></span>
                            </template>
                        </template>
                        <span class="mcq-muted" v-else>Question not added yet, click edit to add it</span>
                    </div>
                    <div class="mcq-card-actions">
                        <a v-for="image in questionImages(question)" :key="image.id" :href="image.url" target="_blank"
                            class="mcq-qimg-wrap">
                            <img class="mcq-qimg" :src="image.url" />
                            <img class="mcq-qimg-preview" :src="image.url" />
                        </a>
                        <span class="mcq-warn" v-if="!question.choices || !question.choices.length">
                            <i class="fas fa-exclamation-triangle mr-1"></i>No choices
                        </span>
                        <span class="mcq-warn" v-else-if="!question.choices.some(isRightChoice)">
                            <i class="fas fa-exclamation-triangle mr-1"></i>Answer not marked
                        </span>
                        <template v-if="differentScores">
                            <span class="mcq-chip mcq-chip-success" title="Score for right answer"
                                v-if="question.maximum_mark != null">+{{ question.maximum_mark }}</span>
                            <span class="mcq-chip mcq-chip-danger" title="Score for wrong answer"
                                v-if="question.wrong_answer_mark != null">-{{ question.wrong_answer_mark }}</span>
                        </template>
                        <span class="text-info btn m-0 p-0" title="Edit"
                            @click="openEditQuestion(question.id)"><i class="far fa-edit"></i></span>
                        <span class="text-danger btn m-0 p-0 ml-2" title="Delete"
                            @click="deleteQuestion(question.id)"><i class="fas fa-trash"></i></span>
                    </div>
                </div>

                <div class="mcq-card-body" v-if="question.choices && question.choices.length">
                    <div class="mcq-choices">
                        <div v-for="choice in question.choices" :key="choice.id"
                            :class="['mcq-choice', { 'mcq-choice-right': isRightChoice(choice) }]">
                            {{ choice.choice_no }}. {{ choice.name }}
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>

        <McqQuestionsExcel class="no-print" :exam="exam" :differentScores="differentScores" :examScores="examScores"
            :nextQuestionNo="nextQuestionNo" @imported="prepareQuestionsView" />

        <!-- Edit question  -->
        <b-modal class="no-print" id="editMcqQuestionModel" no-close-on-backdrop no-close-on-keyboard no-close-on-esc lazy
            size="lg" hide-header hide-footer body-class="p-0">
            <div class="mcq-edit">
                <div class="mcq-edit-head">
                    <span class="mcq-qno">{{ question.question_no }}</span>
                    <span class="mcq-edit-title">Edit Question</span>
                </div>

                <div class="mcq-edit-body">
                    <b-row>
                        <b-col cols="12" :md="differentScores ? 4 : 12">
                            <b-form-group label="Question No" label-class="mcq-label">
                                <b-form-input v-model="question.question_no" type="text" placeholder="1"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="6" md="4" v-if="differentScores">
                            <b-form-group label="Score for right answer" label-class="mcq-label">
                                <b-form-input v-model="question.maximum_mark" type="text" placeholder="1"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="6" md="4" v-if="differentScores">
                            <b-form-group label="Score for wrong answer" label-class="mcq-label">
                                <b-form-input v-model="question.wrong_answer_mark" type="text" placeholder="0"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <div v-if="question.length != 0">
                        <div class="mcq-section-title">Question</div>
                        <div v-for="(qData, qdIndex) in question.question" :key="qdIndex" class="mcq-qpart">
                            <div class="flex-grow-1 min-w-0">
                                <jodit-editor v-if="qData.type == 'text'" v-model="qData.value"
                                    :extra-buttons="customButtons" :config="editorConfig"
                                    :buttons="JoditViewFullButtons" />
                                <div v-if="qData.type == 'equation'">
                                    <math-field v-if="qData.value == ''" :id="'mathfield' + qdIndex" style="width: 100%">
                                        x=\frac{-b\pm \sqrt{b^2-4ac}}{2a}</math-field>
                                    <math-field v-else :id="'mathfield' + qdIndex" style="width: 100%">{{ qData.value
                                    }}</math-field>
                                </div>
                                <div v-if="qData.type == 'image' && questionAttachmentDetails(qData.value)">
                                    <img class="mcq-edit-img" :src="cloudStorageRoot +
                                        questionAttachmentDetails(qData.value).file_path +
                                        questionAttachmentDetails(qData.value).file_name" />
                                    <a :href="cloudStorageRoot +
                                        questionAttachmentDetails(qData.value).file_path +
                                        questionAttachmentDetails(qData.value).file_name" class="mcq-file-link"
                                        target="_blank">
                                        {{ questionAttachmentDetails(qData.value).file_name }}
                                    </a>
                                </div>
                            </div>
                            <span class="text-danger btn m-0 p-0 ml-2" title="Remove"
                                @click="removeDataFromQuestion(qdIndex)"><i class="fas fa-trash"></i></span>
                        </div>

                        <div class="mcq-section-title mt-3">
                            Choices
                            <span class="mcq-section-hint">Tick the right answer</span>
                        </div>
                        <div class="mcq-muted mb-2" v-if="!question.choices || !question.choices.length">
                            No choices yet, add at least two.
                        </div>
                        <div v-for="choice in question.choices" :key="choice.id"
                            :class="['mcq-edit-choice', { 'mcq-choice-right': isRightChoice(choice) }]">
                            <b-form-checkbox class="mcq-right-check" v-model="choice.is_right_choice"
                                @change="handleSelection(choice.id, choice.is_right_choice)"
                                v-b-tooltip.hover title="Right answer"></b-form-checkbox>
                            <b-form-input class="mcq-edit-choice-no" v-model="choice.choice_no" type="text"
                                placeholder="A"></b-form-input>
                            <b-form-input class="flex-grow-1" v-model="choice.name" type="text"
                                placeholder="Enter choice"></b-form-input>
                            <span class="text-danger btn m-0 p-0 ml-2" title="Delete choice"
                                @click="deleteChoice(choice.id)"><i class="fas fa-trash"></i></span>
                        </div>

                        <div class="mt-2">
                            <b-button variant="outline-primary" size="sm" class="mcq-btn" @click="addChoice(question.id)">
                                <i class="fas fa-plus mr-1"></i> Add Choice
                            </b-button>
                            <b-button variant="outline-secondary" size="sm" class="mcq-btn ml-2"
                                @click="addImageToQuestion()">
                                <i class="far fa-image mr-1"></i> Add Image
                            </b-button>
                        </div>
                        <b-form-file :ref="'newQuestionAttachmentInput'" style="display: none"
                            @change="uploadQuestionAttachments" class="text-center"
                            v-model="newQuestionAttAchmentFile"></b-form-file>
                    </div>
                </div>

                <div class="mcq-edit-foot">
                    <span class="mcq-muted" v-if="updatingQuestion">
                        <b-spinner small class="mr-1"></b-spinner> Saving, please wait...
                    </span>
                    <span v-else></span>
                    <div>
                        <b-button variant="light" size="sm" class="mcq-btn" @click="cancelEditQuestion">Cancel</b-button>
                        <b-button variant="primary" size="sm" class="mcq-btn ml-2" :disabled="updatingQuestion"
                            @click="saveQuestion(true)">Save Changes</b-button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import { JoditViewFullButtons } from "../../../../constants";
import { JoditEditor } from "jodit-vue";
import McqQuestionsExcel from "./McqQuestionsExcel.vue";
export default {
    props: ["exam"],
    async created() {
        this.prepareQuestionsView();
        this.getExamScores();
        this.cloudStorageRoot = this.$store.getters.getAPIKey.userAttachments;
    },
    components: {
        JoditEditor,
        McqQuestionsExcel,
    },
    computed: {
        // exam created with "Every question has different scores" keeps positive_mark empty
        differentScores() {
            return this.examScores.positive_mark == null;
        },
        questionList() {
            return Object.values(this.questionObjects);
        },
        // imported questions without a number continue after the last one
        nextQuestionNo() {
            const numbers = this.questionList.map((question) => parseInt(question.question_no)).filter((no) => !isNaN(no));
            return numbers.length ? Math.max(...numbers) + 1 : 1;
        },
    },
    data() {
        return {
            examScores: { positive_mark: null, negative_mark: null },
            questionAttachments: {},//id:{id, url, name}
            fetchData: false,
            enableEvaluationScheme: false,
            questionObjects: {},
            enableQuestionView: true,
            question: [],
            cloudStorageRoot: "",
            updatingQuestion: false,
            newQuestionAttAchmentFile: null,
            enablehoice: false,
            customButtons: [
                {
                    name: "LaTex",
                    iconURL: "",
                    exec: function (editor) {
                        editor.selection.insertHTML("<span> $E=mc^2$ </span>");
                    },
                },
            ],
            editorConfig: {
                toolbarButtonSize: "xsmall",
                enter: "br",
            },
            JoditViewFullButtons: JoditViewFullButtons,
        };
    },
    methods: {
        async getExamScores() {
            const url = this.$store.getters.getAPIKey.examination + `/${this.exam.id}`;
            await this.$axios
                .get(url)
                .then((response) => {
                    this.examScores = {
                        positive_mark: response.data.positive_mark,
                        negative_mark: response.data.negative_mark,
                    };
                })
                .catch((error) => {
                    error;
                });
        },
        markToString(mark) {
            return mark == null || mark === "" ? null : `${mark}`;
        },
        latexCodeToMathLive(codeText) {
            if (codeText == null || codeText == "") return "";
            return codeText.replace(
                /\$\$(.*?)\$\$/g,
                '<math-field class="p-0" style="border: none;" readonly>$1</math-field>'
            );
        },
        isRightChoice(choice) {
            return [true, 1, "1", "true"].includes(choice.is_right_choice);
        },
        questionImages(question) {
            if (!Array.isArray(question.question)) return [];
            return question.question
                .filter((qData) => qData.type == "image" && this.questionAttachments[qData.value])
                .map((qData) => this.questionAttachments[qData.value]);
        },
        hasQuestionText(question) {
            return Array.isArray(question.question) &&
                question.question.some((qData) => qData.type == "image" || (qData.value && qData.value.trim() != ""));
        },
        // checkbox works with true/false, the api returns 1/0
        normalizeChoices(choices) {
            (choices || []).forEach((choice) => {
                choice.is_right_choice = this.isRightChoice(choice);
            });
            return choices;
        },
        async openEditQuestion(questionId) {
            this.editQuestion = this.questionObjects[questionId];
            this.question = this.editQuestion;
            this.normalizeChoices(this.question.choices);
            if (!this.question.question) {
                this.addTextToQuestion();
            }
            this.$bvModal.show("editMcqQuestionModel");
        },
        handleSelection(choice_id, right) {
            if (right) {
                this.question.choices.forEach((choice) => {
                    if (choice.id != choice_id) {
                        choice.is_right_choice = false;
                    }
                });
            }
        },
        async deleteAllQuestions(ExamId) {
            if (
                confirm(
                    "Are you sure you want to delete all questions? Deleting questions will also delete the choices, answer, attachment & score"
                )
            ) {
                this.fetchData = true;
                const url =
                    this.$store.getters.getAPIKey.mainAPI +
                    `/examquestions/delete?exam_id=${ExamId}`;

                await this.$axios
                    .delete(url)
                    .then(() => {
                        this.prepareQuestionsView();
                    })
                    .catch((error) => {
                        error;
                    });
                this.fetchData = false;
            }
        },
        async uploadQuestionAttachments(event) {
            event;
            await this.saveQuestion(false);
            this.updatingQuestion = true;
            const fd = await new FormData();
            await fd.append("attachment", this.newQuestionAttAchmentFile);
            const url =
                this.$store.getters.getAPIKey.mainAPI +
                `/examquestions/${this.question.id}/attachments`;
            await this.$axios
                .post(url, fd)
                .then(() => {
                    this.getQuestion(this.question.id);
                })
                .catch((error) => {
                    error;
                    this.$toast.error("Failed to attach, please try again");
                });

            this.updatingQuestion = false;
        },
        async getQuestion(questionId) {
            const url =
                this.$store.getters.getAPIKey.mainAPI +
                `/examquestions/${questionId}`;
            await this.$axios.get(url).then((response) => {
                let question = response.data;
                this.question = {
                    files: question.files,
                    id: question.id,
                    maximum_mark: question.maximum_mark,
                    wrong_answer_mark: question.wrong_answer_mark,
                    choices: this.normalizeChoices(question.choices),
                    question: question.question,
                    question_no: question.question_no,
                    summary: question.summary,
                    updated_at: question.updated_at,
                };
            });
        },
        addImageToQuestion() {
            this.$refs["newQuestionAttachmentInput"].$refs.input.click();
        },
        async deleteQuestion(questionId) {
            if (
                confirm(
                    "Are you sure you want to delete this questions? Deleting questions will also delete the choices, answer, attachment, score."
                )
            ) {
                this.fetchData = true;
                const url =
                    this.$store.getters.getAPIKey.mainAPI +
                    `/examquestions/${questionId}`;
                await this.$axios
                    .delete(url)
                    .then(() => {
                        this.prepareQuestionsView();
                    })
                    .catch((error) => {
                        error;
                    });

                this.fetchData = false;
            }
        },
        async deleteChoice(choiceId) {
            await this.saveQuestion(false);
            if (confirm("Are you sure you want to delete this choices?")) {
                this.fetchData = true;
                const url =
                    this.$store.getters.getAPIKey.mainAPI +
                    `/examchoices/${choiceId}`;
                await this.$axios
                    .delete(url)
                    .then(() => {
                        this.getQuestion(this.question.id);
                    })
                    .catch((error) => {
                        error;
                    });
                this.fetchData = false;
            }
        },
        printQuestionPaper() {
            this.enableEvaluationScheme = false;
            this.enableQuestionView = true;
            setTimeout(() => {
                window.print();
            }, 200);
            setTimeout(() => {
                this.enableEvaluationScheme = true;
                this.enableQuestionView = true;
            }, 1000);
        },
        async addChoice(quastionId) {
            this.fetchData = true;
            await this.saveQuestion(false);
            let postData = {
                question_id: quastionId,
            };
            const url = this.$store.getters.getAPIKey.mainAPI + `/examchoices`;
            await this.$axios
                .post(url, postData)
                .then(() => { })
                .catch((error) => {
                    this.$toast.error(
                        "Failed to add question, Please try again. (" +
                        error.response.data.message +
                        ")"
                    );
                });
            this.getQuestion(this.question.id);
            this.fetchData = false;
        },
        async addNewQuestion() {
            this.fetchData = true;
            let postData = {
                dummy_question: true,
                exam_id: this.exam.id,
            };
            const url =
                this.$store.getters.getAPIKey.mainAPI + `/examquestions`;
            await this.$axios
                .post(url, postData)
                .then(() => { })
                .catch((error) => {
                    this.$toast.error(
                        "Failed to add question, Please try again. (" +
                        error.response.data.message +
                        ")"
                    );
                });
            await this.prepareQuestionsView();
            this.fetchData = false;
        },
        async prepareQuestionsView() {
            this.questions = [];
            this.questionObjects = {};
            await Promise.all([this.getExamQuestions()]);
            this.fetchData = true;
            const questionObjects = {};
            this.questions.forEach((question) => {
                questionObjects[question.id] = question;
            });
            const questionAttachments = {};
            this.questions.forEach(question => {
                (question.files || []).forEach(file => {
                    questionAttachments[file.id] = {
                        id: file.id, name: file.file_name, url: this.cloudStorageRoot + file.file_path + file.file_name
                    };
                });
            });
            this.questionAttachments = questionAttachments;
            this.questionObjects = questionObjects;
            this.fetchData = false;
        },
        async getExamQuestions() {
            this.fetchData = true;
            const url =
                this.$store.getters.getAPIKey.examination +
                `/${this.exam.id}/examquestions`;
            await this.$axios
                .get(url)
                .then((response) => {
                    const questions = response.data;
                    this.questions = questions;
                })
                .catch((error) => {
                    error;
                });

            this.fetchData = false;
        },
        cancelEditQuestion() {
            this.$bvModal.hide("editMcqQuestionModel");
            this.question = [];
        },
        questionAttachmentDetails(attachmentId) {
            return this.question.files.find((item) => item.id == attachmentId);
        },
        addTextToQuestion() {
            if (!this.question.question) {
                this.question.question = [];
            }
            this.question.question.push({
                type: "text",
                value: "",
            });
            this.$forceUpdate();
        },
        addEquationToQuestion() {
            if (!this.question.question) {
                this.question.question = [];
            }
            this.question.question.push({
                type: "equation",
                value: "",
            });
            this.$forceUpdate();

            // this.$forceUpdate();
        },
        removeDataFromQuestion(index) {
            this.question.question.splice(index, 1);
            this.$forceUpdate();
        },
        async finishEditQuestion() {
            this.$bvModal.hide("editMcqQuestionModel");
        },
        async saveQuestion(finishEditing) {
            this.updatingQuestion = true;
            if (this.question.question) {
                this.question.question.forEach((qData, qIndex) => {
                    if (qData.type == "equation") {
                        const mf = document.getElementById("mathfield" + qIndex);
                        this.question.question[qIndex].value = mf.value;
                    }
                });
            }
            let examQues = await {
                // same scores for every question come from the exam settings
                maximum_mark: this.markToString(this.differentScores ? this.question.maximum_mark : this.examScores.positive_mark),
                wrong_answer_mark: this.markToString(this.differentScores ? this.question.wrong_answer_mark : this.examScores.negative_mark),
                question: this.question.question,
                question_no: this.question.question_no,
                choices: this.question.choices,
            };
            const url =
                this.$store.getters.getAPIKey.mainAPI + `/examquestions/${this.question.id}`;
            await this.$axios
                .put(url, examQues)
                .then(() => {
                    if (finishEditing) this.finishEditQuestion();
                })
                .catch((error) => {
                    this.$toast.error(
                        "Failed to save question, Please try again. (" +
                        error.response.data.message +
                        ")"
                    );
                });
            this.prepareQuestionsView();
            this.updatingQuestion = false;
        },
    },
};
</script>

<style scoped>
.mcq-paper {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 4px 12px;
    font-size: 13px;
    color: #2d3748;
}

/* app wide icon sizes are large, keep them in line with the text here */
.mcq-paper i,
.mcq-edit i {
    font-size: 12px !important;
}

.mcq-loading {
    text-align: center;
    padding: 32px 0;
    color: #718096;
}

.mcq-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: linear-gradient(135deg, #eef4ff 0%, #f8fbff 100%);
    border: 1px solid #dbe6fb;
    border-radius: 8px;
}

.mcq-header > div:first-child {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.mcq-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a365d;
}

.mcq-subtitle {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.mcq-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    color: #4a5568;
    background: #edf2f7;
    border-radius: 999px;
    white-space: nowrap;
}

.mcq-chip-success {
    color: #22753a;
    background: #e3f6e8;
}

.mcq-chip-danger {
    color: #b42318;
    background: #fdecea;
}

.mcq-btn {
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 600;
}

.mcq-icon-btn {
    width: 26px;
    height: 26px;
    padding: 0;
    border-radius: 6px;
    flex-shrink: 0;
}

.mcq-empty {
    text-align: center;
    padding: 28px 16px;
    background: #fff;
    border: 2px dashed #d5deeb;
    border-radius: 8px;
}

.mcq-empty > i {
    font-size: 28px !important;
    color: #a0b4d0;
}

.mcq-empty-title {
    margin-top: 6px;
    font-size: 14px;
    font-weight: 700;
}

.mcq-empty-text,
.mcq-muted {
    font-size: 12px;
    color: #718096;
}

.mcq-header-actions {
    display: flex;
    align-items: center;
}

/* one panel, questions split by a thin line */
.mcq-list {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.mcq-card {
    padding: 6px 10px;
    border-bottom: 1px solid #edf2f7;
}

.mcq-card:last-child {
    border-bottom: none;
}

.mcq-card:hover {
    background: #fafcff;
}

.mcq-card-body {
    margin: 2px 0 0 24px;
}

.mcq-card-head {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.mcq-qno-plain {
    min-width: 16px;
    padding-top: 2px;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
}

.mcq-qno {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: #3b6fd8;
    border-radius: 5px;
    flex-shrink: 0;
}

.mcq-qtext {
    flex: 1;
    min-width: 0;
    padding-top: 2px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    word-break: break-word;
}

/* small thumbnail in the question row, larger preview on hover, opens full size on click */
.mcq-qimg-wrap {
    position: relative;
    display: block;
}

.mcq-qimg {
    display: block;
    width: 26px;
    height: 26px;
    object-fit: cover;
    border: 1px solid #cbd5e0;
    border-radius: 5px;
}

.mcq-qimg-preview {
    display: none;
    position: absolute;
    top: 0;
    right: calc(100% + 8px);
    max-width: 320px;
    max-height: 320px;
    padding: 4px;
    background: #fff;
    border: 1px solid #cbd5e0;
    border-radius: 8px;
    box-shadow: 0 6px 20px rgba(16, 24, 40, 0.25);
    z-index: 20;
    pointer-events: none;
}

.mcq-qimg-wrap:hover .mcq-qimg-preview {
    display: block;
}

.mcq-card-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

/* same as the public exam paper: right choice green, others red */
.mcq-choices {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 16px;
}

.mcq-choice {
    font-size: 12px;
    color: #dc3545;
    word-break: break-word;
}

.mcq-choice-right {
    color: #28a745;
}

.mcq-warn {
    font-size: 11px;
    font-weight: 600;
    color: #b7791f;
    white-space: nowrap;
}

/* edit popup */
.mcq-edit-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px;
    border-bottom: 1px solid #e2e8f0;
}

.mcq-edit-title {
    font-size: 17px;
    font-weight: 700;
}

.mcq-edit-body {
    max-height: 65vh;
    overflow-y: auto;
    padding: 16px 20px;
}

.mcq-edit-body ::v-deep .mcq-label {
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
}

.mcq-section-title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #1a365d;
}

.mcq-section-hint {
    font-size: 12px;
    font-weight: 400;
    color: #718096;
}

.mcq-qpart {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
}

.min-w-0 {
    min-width: 0;
}

.mcq-edit-img {
    display: block;
    max-width: 100%;
    max-height: 300px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.mcq-file-link {
    font-size: 12px;
    font-weight: 600;
    word-break: break-word;
}

.mcq-edit-choice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #f7f9fc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
}

.mcq-edit-choice-no {
    width: 64px;
    flex-shrink: 0;
    text-align: center;
}

.mcq-right-check {
    margin-left: 4px;
}

.mcq-edit-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-top: 1px solid #e2e8f0;
    background: #fafbfd;
    border-radius: 0 0 0.3rem 0.3rem;
}

@media (max-width: 576px) {
    .mcq-card-body {
        margin-left: 0;
    }
}
</style>
